import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';

// SHIPM8 "Rail & Peek" theme pass (design handoff, "Step 1-2, streamlined").
// Everything PrimeNG renders is styled here, through tokens; only what
// PrimeNG doesn't render (page text, layout, cards) gets central classes in
// the `look` stylesheet layer (see libs/theme/src/styles).
//
// Colour values below are copied from the handoff's tokens.css. They're
// literal hex (or color-mix() built from those hex values), not CSS custom
// properties: this object is consumed by PrimeNG's theme engine, which is a
// separate system from the hand-authored `--sm-*` custom properties in
// _tokens.scss, even though both describe the same palette.
//
// Exception: a component token that sits outside a `colorScheme` block (so
// it applies in light and dark alike) and needs a different colour per mode
// is a `var(--sm-*)` instead. PrimeNG writes the value into its own custom
// property as-is, so it resolves at runtime and follows `sm-dark`.

// -- Brand ------------------------------------------------------------------
const SM_NAVY = '#0C365A';
const SM_AMBER = '#F59300';
const SM_AMBER_DARK = '#D68305';
const SM_INK = '#0B1622';

// -- Status (fed into Tag: fill = 15% tint, text = 60% shade) ---------------
const SM_STATUS_SUCCESS = '#2E9E5B';
const SM_STATUS_WARNING = '#D68305';
const SM_STATUS_DANGER = '#D0342C';
const SM_STATUS_INFO = '#2F6FB0';
const SM_STATUS_NEUTRAL = '#8A98A5';

// Amber focus ring shadow, per the handoff's `--sm-focus-ring`.
const SM_FOCUS_RING_SHADOW = '0 0 0 3px rgba(245, 147, 0, 0.2)';

// Text shades go toward black on the light scheme and toward white on the
// dark one, so the text stays readable on either page colour.
type Toward = 'black' | 'white';

/**
 * Tag severity colours: 15% fill (status colour at 15% over whatever's
 * behind it) and a 60% shade (status colour mixed 40% toward black, or white
 * in dark) for readable text. The border (40% tint) has no PrimeNG token to
 * hang off -- see _primeng-overrides.scss for that half of the tint.
 */
function tintedSeverity(hex: string, toward: Toward = 'black') {
  return {
    background: `color-mix(in srgb, ${hex}, transparent 85%)`,
    color: `color-mix(in srgb, ${hex} 60%, ${toward})`,
  };
}

/**
 * Button severity colours, calmer than Lara's bright defaults: built from the
 * same status colours as the KPI cards and timeline (review 2026-10-12).
 * Solid: status colour, darker on hover/active, white text.
 */
function solidSeverity(hex: string) {
  const hover = `color-mix(in srgb, ${hex}, black 12%)`;
  const active = `color-mix(in srgb, ${hex}, black 22%)`;
  return {
    background: hex,
    hoverBackground: hover,
    activeBackground: active,
    borderColor: hex,
    hoverBorderColor: hover,
    activeBorderColor: active,
    color: '#ffffff',
    hoverColor: '#ffffff',
    activeColor: '#ffffff',
    focusRing: {
      color: 'transparent',
      shadow: `0 0 0 0.2rem color-mix(in srgb, ${hex}, transparent 70%)`,
    },
  };
}

/** Outlined button: soft border, shaded text, faint tint on hover. */
function outlinedSeverity(hex: string, toward: Toward = 'black') {
  return {
    hoverBackground: `color-mix(in srgb, ${hex}, transparent 92%)`,
    activeBackground: `color-mix(in srgb, ${hex}, transparent 85%)`,
    borderColor: `color-mix(in srgb, ${hex}, transparent 50%)`,
    color: `color-mix(in srgb, ${hex} 80%, ${toward})`,
  };
}

/** Text button: shaded text, faint tint on hover. */
function textSeverity(hex: string, toward: Toward = 'black') {
  return {
    hoverBackground: `color-mix(in srgb, ${hex}, transparent 92%)`,
    activeBackground: `color-mix(in srgb, ${hex}, transparent 85%)`,
    color: `color-mix(in srgb, ${hex} 80%, ${toward})`,
  };
}

// Navy ramp around the SHIPM8 "next" brand colour (--sm-navy, #0C365A at 500).
// Lighter steps tint toward --sm-mist (#EEF2F5) / white, darker steps go down
// to --sm-navy-deep (#072136) around 800 and darker still below that.
export const Shipm8Preset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#F4F7F9',
      100: '#EEF2F5',
      200: '#D3DEE6',
      300: '#A8BDCC',
      400: '#6E8DA3',
      500: SM_NAVY,
      600: '#0A2E4D',
      700: '#092740',
      800: '#072136',
      900: '#051A29',
      950: '#03111C',
    },
    // Top-level outline is left off (Lara's own default: width 0, style
    // none): the visible ring is the amber box-shadow below. Setting both
    // would draw two rings.
    //
    // Control heights (button, inputtext, select) are NOT driven by
    // paddingY any more: Chrome enforces a minimum line box for a
    // single-line text <input> based on the font's normal line-height, so
    // padding + line-height arithmetic can't hit an exact height for
    // inputs. _primeng-overrides.scss sets those three an explicit
    // height/min-height (from --sm-control-sm/md/lg) with padding-block: 0
    // instead. `paddingY` below only still matters for `textarea` (left out
    // of that override, so it keeps a normal, readable line-height) --
    // `fontSize` on `sm`/`lg` still applies to all four (button/inputtext/
    // select/textarea alike).
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.625rem',
      sm: {
        fontSize: '13px',
        paddingX: '0.625rem',
        paddingY: '0.5rem',
      },
      lg: {
        fontSize: '16px',
        paddingX: '0.875rem',
        paddingY: '0.75rem',
      },
      borderRadius: '{border.radius.md}', // 6px, Lara's own default.
    },
    colorScheme: {
      light: {
        // Neutral ramp (design handoff tokens.css): paper -> surface-subtle
        // -> mist -> row-divider -> border -> border-input -> faint ->
        // muted -> slate -> text-2 -> ink.
        surface: {
          0: '#FFFFFF',
          50: '#F7F9FB',
          100: '#EEF2F5',
          200: '#F0F3F6',
          300: '#DCE3EA',
          400: '#C3CDD6',
          500: '#A5B0BA',
          600: '#7D8B98',
          700: '#5B6B7A',
          800: '#33414F',
          900: '#0B1622',
          950: '#0B1622',
        },
        text: {
          color: '{surface.900}', // ink
          hoverColor: '{surface.900}',
          mutedColor: '{surface.700}', // slate
          hoverMutedColor: '{surface.800}',
        },
        content: {
          // Drives datatable/card/overlay border colours by default.
          borderColor: '{surface.300}',
        },
        focusRing: {
          shadow: SM_FOCUS_RING_SHADOW,
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.50}', // surface-subtle
          borderColor: '{surface.400}', // border-input
          hoverBorderColor: '{primary.color}',
          focusBorderColor: SM_AMBER,
          invalidBorderColor: SM_STATUS_DANGER,
          color: '{text.color}',
          disabledColor: '{surface.700}', // slate
          placeholderColor: '{surface.600}', // muted
          invalidPlaceholderColor: SM_STATUS_DANGER,
          iconColor: '{surface.700}', // slate
        },
      },
      // Dark (class `sm-dark`, see darkModeSelector in providePrimeNG). The
      // ramp runs the same way as light (0 lightest, 950 darkest), because
      // Lara's dark defaults for every component we don't override pick from
      // its dark end: 900 = content background, 800 = headers and hover,
      // 700 = borders, 0 = text. Same hex as the dark --sm-* tokens:
      // 0 ink, 100 text-2, 300 slate, 400 muted, 500 faint, 600 border-input,
      // 700 border, 800 row-divider, 900 paper, 950 canvas (mist).
      dark: {
        surface: {
          0: '#E6ECF1',
          50: '#D5DDE4',
          100: '#C3CED8',
          200: '#ABB8C4',
          300: '#93A3B1',
          400: '#74838F',
          500: '#56636E',
          600: '#384755',
          700: '#26333F',
          800: '#1C2833',
          900: '#121C26',
          950: '#0B131B',
        },
        // Plain navy is too dark to read on a dark page.
        primary: {
          color: '#3D73A6',
          contrastColor: '#FFFFFF',
          hoverColor: '#4A82B6',
          activeColor: '#5B8FBF',
        },
        text: {
          color: '{surface.0}', // ink
          hoverColor: '{surface.0}',
          mutedColor: '{surface.300}', // slate
          hoverMutedColor: '{surface.100}',
        },
        content: {
          background: '{surface.900}', // paper
          hoverBackground: '{surface.800}',
          borderColor: '{surface.700}', // border
        },
        focusRing: {
          shadow: SM_FOCUS_RING_SHADOW,
        },
        formField: {
          background: '{surface.950}',
          disabledBackground: '{surface.800}',
          borderColor: '{surface.600}', // border-input
          hoverBorderColor: '{primary.color}',
          focusBorderColor: SM_AMBER,
          invalidBorderColor: SM_STATUS_DANGER,
          color: '{text.color}',
          disabledColor: '{surface.300}', // slate
          placeholderColor: '{surface.400}', // muted
          invalidPlaceholderColor: SM_STATUS_DANGER,
          iconColor: '{surface.300}', // slate
        },
      },
    },
  },
  components: {
    button: {
      root: {
        label: {
          fontWeight: '500',
        },
      },
      colorScheme: {
        light: {
          // Secondary was Lara's default (light grey text on light grey),
          // too pale to read as a button (review 2026-09-30): a light
          // surface with dark text and a visible border.
          root: {
            secondary: {
              background: '{surface.200}',
              hoverBackground: '{surface.300}',
              activeBackground: '{surface.400}',
              borderColor: '{surface.300}',
              hoverBorderColor: '{surface.400}',
              activeBorderColor: '{surface.500}',
              color: '{surface.900}', // ink
              hoverColor: '{surface.900}',
              activeColor: '{surface.900}',
            },
            success: solidSeverity(SM_STATUS_SUCCESS),
            info: solidSeverity(SM_STATUS_INFO),
            warn: solidSeverity(SM_STATUS_WARNING),
            danger: solidSeverity(SM_STATUS_DANGER),
          },
          outlined: {
            // White (transparent-over-white) + border-input + text-2, per
            // the handoff's "Outlined button" spec.
            primary: {
              hoverBackground: '{surface.100}', // mist
              activeBackground: '{surface.200}',
              borderColor: '{surface.400}', // border-input
              color: '{surface.800}', // text-2
            },
            secondary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              borderColor: '{surface.500}',
              color: '{surface.700}', // slate
            },
            success: outlinedSeverity(SM_STATUS_SUCCESS),
            info: outlinedSeverity(SM_STATUS_INFO),
            warn: outlinedSeverity(SM_STATUS_WARNING),
            danger: outlinedSeverity(SM_STATUS_DANGER),
          },
          text: {
            // Slate text, per the handoff's "Text button" spec.
            primary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              color: '{surface.700}', // slate
            },
            secondary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              color: '{surface.700}', // slate
            },
            success: textSeverity(SM_STATUS_SUCCESS),
            info: textSeverity(SM_STATUS_INFO),
            warn: textSeverity(SM_STATUS_WARNING),
            danger: textSeverity(SM_STATUS_DANGER),
          },
        },
        dark: {
          // The light specs mirrored onto the dark ramp (see the semantic
          // dark block); the severities shade toward white.
          root: {
            secondary: {
              background: '{surface.800}',
              hoverBackground: '{surface.700}',
              activeBackground: '{surface.600}',
              borderColor: '{surface.700}',
              hoverBorderColor: '{surface.600}',
              activeBorderColor: '{surface.500}',
              color: '{surface.0}', // ink
              hoverColor: '{surface.0}',
              activeColor: '{surface.0}',
            },
            success: solidSeverity(SM_STATUS_SUCCESS),
            info: solidSeverity(SM_STATUS_INFO),
            warn: solidSeverity(SM_STATUS_WARNING),
            danger: solidSeverity(SM_STATUS_DANGER),
          },
          outlined: {
            primary: {
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              borderColor: '{surface.600}', // border-input
              color: '{surface.100}', // text-2
            },
            secondary: {
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              borderColor: '{surface.500}',
              color: '{surface.300}', // slate
            },
            success: outlinedSeverity(SM_STATUS_SUCCESS, 'white'),
            info: outlinedSeverity(SM_STATUS_INFO, 'white'),
            warn: outlinedSeverity(SM_STATUS_WARNING, 'white'),
            danger: outlinedSeverity(SM_STATUS_DANGER, 'white'),
          },
          text: {
            primary: {
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              color: '{surface.300}', // slate
            },
            secondary: {
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              color: '{surface.300}', // slate
            },
            success: textSeverity(SM_STATUS_SUCCESS, 'white'),
            info: textSeverity(SM_STATUS_INFO, 'white'),
            warn: textSeverity(SM_STATUS_WARNING, 'white'),
            danger: textSeverity(SM_STATUS_DANGER, 'white'),
          },
        },
      },
    },
    checkbox: {
      // px, not rem: the app's root font-size is 14px, so 1rem is 14px here.
      root: {
        width: '16px', // per the handoff
        height: '16px',
        sm: { width: '14px', height: '14px' },
        lg: { width: '20px', height: '20px' },
        // Checked = amber (decided 2026-09-24, for visual interest; an
        // exception to "amber is a pointer only").
        checkedBackground: SM_AMBER,
        checkedHoverBackground: SM_AMBER_DARK,
        checkedBorderColor: SM_AMBER,
        checkedHoverBorderColor: SM_AMBER_DARK,
        checkedFocusBorderColor: SM_AMBER,
      },
      icon: {
        // Smaller than the box so the check has breathing room.
        size: '10px',
        // White check on amber (decided 2026-09-24). A deliberate exception
        // to the brand guideline against white on amber, which is about text.
        checkedColor: '#FFFFFF',
        checkedHoverColor: '#FFFFFF',
      },
    },
    toggleswitch: {
      // Smaller than PrimeNG's 40x24 default, which filled the whole line
      // height and stacked switches edge to edge (review 2026-09-28).
      root: {
        width: '34px',
        height: '18px',
        gap: '3px',
      },
      handle: {
        size: '12px',
      },
      colorScheme: {
        light: {
          // Checked track = amber, like the checkbox. The handle stays white
          // (it's a shape, not text on amber).
          root: {
            checkedBackground: SM_AMBER,
            checkedHoverBackground: SM_AMBER_DARK,
          },
          handle: {
            checkedColor: SM_INK,
            checkedHoverColor: SM_INK,
          },
        },
        dark: {
          // Same as light.
          root: {
            checkedBackground: SM_AMBER,
            checkedHoverBackground: SM_AMBER_DARK,
          },
          handle: {
            checkedColor: SM_INK,
            checkedHoverColor: SM_INK,
          },
        },
      },
    },
    // sh-segmented (SelectButton of ToggleButtons): the connected buttons
    // keep the form-field look. The selected one is a neutral grey here; a
    // per-option severity colour has no token (PrimeNG gives every option
    // the same styleClass), so `.sh-segmented` in _components.scss tints it.
    // Heights come from the same --sm-control-* scale as buttons (set there).
    selectbutton: {
      root: {
        borderRadius: '{form.field.border.radius}',
      },
    },
    togglebutton: {
      root: {
        padding: '0 0.75rem',
        borderRadius: '{form.field.border.radius}',
        fontWeight: '500',
        gap: '0.4rem',
        checkedBackground: 'var(--sm-border)',
        checkedBorderColor: 'var(--sm-border-input)',
        checkedColor: 'var(--sm-ink)',
        hoverColor: 'var(--sm-ink)',
        sm: {
          fontSize: '13px',
          padding: '0 0.625rem',
        },
        lg: {
          fontSize: '16px',
          padding: '0 0.875rem',
        },
      },
      icon: {
        checkedColor: 'currentColor',
      },
    },
    tabs: {
      // PrimeNG's Tabs draws the active indicator two ways at once: the
      // active tab's own border (tab.borderWidth/activeBorderColor) *and* a
      // separate absolutely-positioned `.p-tablist-active-bar` element
      // (activeBar.height/background). Using both stacks two indicators (a
      // border rendered under a 1px-antialiased sub-pixel border box gave a
      // wrong effective height in review). Drive the underline through
      // `activeBar` only -- it's a plain, unambiguous height/colour, not
      // rasterised as a border -- and switch the tab's own border off.
      tab: {
        borderWidth: '0',
        fontWeight: '500',
        color: 'var(--sm-slate)', // inactive
        hoverColor: 'var(--sm-ink)',
        activeColor: 'var(--sm-navy)', // navy (active); light blue in dark
      },
      activeBar: {
        height: '3px',
        bottom: '0',
        background: SM_AMBER,
      },
      // The scroll arrows' glow: Lara's is a 50px white spread on all sides,
      // which washed out everything near the arrow (e.g. the detail header's
      // key facts above the tab bar). A narrow fade towards the tabs only.
      colorScheme: {
        light: {
          navButton: {
            shadow: '-14px 0 10px -6px #ffffff',
          },
        },
        dark: {
          navButton: {
            shadow: '-14px 0 10px -6px #121c26',
          },
        },
      },
    },
    tag: {
      root: {
        borderRadius: '4px',
      },
      colorScheme: {
        light: {
          success: tintedSeverity(SM_STATUS_SUCCESS),
          info: tintedSeverity(SM_STATUS_INFO),
          warn: tintedSeverity(SM_STATUS_WARNING),
          danger: tintedSeverity(SM_STATUS_DANGER),
          secondary: tintedSeverity(SM_STATUS_NEUTRAL),
        },
        dark: {
          success: tintedSeverity(SM_STATUS_SUCCESS, 'white'),
          info: tintedSeverity(SM_STATUS_INFO, 'white'),
          warn: tintedSeverity(SM_STATUS_WARNING, 'white'),
          danger: tintedSeverity(SM_STATUS_DANGER, 'white'),
          secondary: tintedSeverity(SM_STATUS_NEUTRAL, 'white'),
        },
      },
    },
    datatable: {
      // Row heights are NOT driven by padding any more: a cell's content
      // can be taller than the text the padding was sized for (the
      // header's select-all checkbox; a two-line body cell), which broke
      // the padding-arithmetic approach. _primeng-overrides.scss sets an
      // explicit 46px/64px height with padding-block: 0 instead, so only
      // the horizontal padding below still applies (and the header's
      // 11px/uppercase text, which PrimeNG has no token for, also lives
      // there).
      headerCell: {
        padding: '0 20px',
      },
      bodyCell: {
        padding: '0 20px',
        borderColor: 'var(--sm-row-divider)', // lighter than content.borderColor
      },
      colorScheme: {
        light: {
          row: {
            selectedBackground: '#FEF5E8', // --sm-row-selected
            // The amber inset bar has no token either; see
            // _primeng-overrides.scss.
          },
        },
        dark: {
          row: {
            selectedBackground: '#2A2114', // --sm-row-selected (dark)
          },
        },
      },
    },
    // Menu (used by sh-action-menu) needs no token overrides: its defaults
    // already resolve to the content/text colours above. The danger item
    // variant has no per-item-severity token at all -- see
    // _primeng-overrides.scss.
    paginator: {
      // Step 5: the paginator sits in the table card's footer, on the
      // subtle surface, with sm-sized nav buttons. Unlike most components,
      // PrimeNG's paginator tokens have no light/dark colorScheme split (see
      // node_modules/@primeng/themes/lara/paginator), so the background goes
      // directly on `root`. The top border has no token -- see
      // `.table-card .p-paginator` in _table.scss.
      root: {
        padding: '8px 20px',
        background: 'var(--sm-surface-subtle)',
      },
      navButton: {
        width: '32px', // --sm-control-sm
        height: '32px',
      },
    },
    // Drawer (sh-drawer, e.g. "All filters"): the title like a dialog title
    // (18/500), not Lara's 24/600. Font and colour have no token -- see
    // _primeng-overrides.scss.
    drawer: {
      title: {
        fontSize: '18px',
        fontWeight: '500',
      },
    },
  },
});
